import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  BrowserRouter as Router,
} from "react-router-dom";
import NotLoggedInArea from "./NotLoggedInArea";
import NavbarComponent from "./components/NavbarComponent";
import LoggedInArea from "./LoggedInArea";
import { Button, ConfigProvider, theme } from "antd";
import axios from "./axios"; // Assuming this is a custom axios instance
import { BulbOutlined } from "@ant-design/icons";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { applyPalette, palettes } from "./theme";

const { defaultAlgorithm, darkAlgorithm } = theme;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading, false = not auth, true = auth
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme = localStorage.getItem("isDarkMode");
    return JSON.parse(storedTheme || "false");
  });

  // Function to check authentication status
  const checkAuth = async () => {
    try {
      const res = await axios.get("/api/isAuth", { withCredentials: true });
      if (res.data?.isAuth === true) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        redirectToLogin();
      }
    } catch (err) {
      if (err.response?.status === 401) {
        const data = err.response.data;
        if (data.refresh) {
          // Token expired, attempt to refresh
          try {
            await axios.get("/api/refresh", { withCredentials: true });
            const retryRes = await axios.get("/api/isAuth", { withCredentials: true });
            if (retryRes.data?.isAuth === true) {
              setIsAuthenticated(true);
              return;
            }
          } catch (refreshErr) {
            console.log("Refresh failed:", refreshErr);
          }
        }
        // If refresh fails or no refresh flag, redirect to login
        setIsAuthenticated(false);
        redirectToLogin();
      } else {
        console.log("Auth check error:", err);
        setIsAuthenticated(false);
      }
    }
  };

  // Redirect to login unless on specific pages
  const redirectToLogin = () => {
    if (
      window.location.pathname !== "/terms-of-service" &&
      window.location.pathname !== "/privacy-policy" &&
      window.location.pathname !== "/auth/login"
    ) {
      navigate("/auth/login");
    }
  };

  // Run auth check on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Theme toggle
  const toggleTheme = () => {
    setIsDarkMode((prevMode) => {
      const newMode = !prevMode;
      localStorage.setItem("isDarkMode", JSON.stringify(newMode));
      return newMode;
    });
  };

  const palette = isDarkMode ? palettes.dark : palettes.light;

  // Paint the palette onto <html> as CSS custom properties.
  useEffect(() => {
    applyPalette(isDarkMode);
  }, [isDarkMode]);

  // Show loading state while auth is being checked
  if (isAuthenticated === null) {
    return <div className="app-loading">Loading…</div>;
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
        token: {
          colorPrimary: palette.accent,
          colorBgBase: palette.bg,
          colorBgContainer: palette.surface,
          colorTextBase: palette.text,
          colorBorder: palette.border,
          fontFamily: "var(--font)",
          borderRadius: 6,
        },
      }}
    >
      <NavbarComponent />
      <Routes>
        <Route path="/" element={<Navigate to="/app/dashboard" />} />
        <Route
          path="/app/*"
          element={
            isAuthenticated ? (
              <LoggedInArea />
            ) : (
              <Navigate to="/auth/login" />
            )
          }
        />
        <Route
          path="/auth/*"
          element={
            isAuthenticated ? (
              <Navigate to="/app/dashboard" />
            ) : (
              <NotLoggedInArea />
            )
          }
        />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
      <Button
        type="primary"
        shape="circle"
        icon={<BulbOutlined />}
        onClick={toggleTheme}
        className="theme-toggle-button"
      />
    </ConfigProvider>
  );
}

// Wrap App with Router if not already done elsewhere
export default function AppWithRouter() {
  return (
    <App />
  );
}